/**
 * Opt-in debug log of the rail follower, for a user to send back from their
 * browser when a trace stops where it should not.
 *
 * Turn it on (then run the trace again):
 *   localStorage.setItem('railTraceDebug', '1')   // reads, failures, retries, ends, crossings
 *   localStorage.setItem('railTraceDebug', '2')   // + every station's heads
 * or add `?railTraceDebug=1` (or `=2`) to the page URL. Off again:
 *   localStorage.removeItem('railTraceDebug')
 *
 * Then, in the console: `__railTraceDebug.copy()` puts the log (JSON) on the
 * clipboard; `__railTraceDebug.dump()` returns it as a string.
 *
 * Off (the default) it costs one integer test per call site: the level is
 * read from storage once per trace (`refreshRailTraceDebug`), and call sites
 * that build data guard with `railTraceDebugLevel()` first.
 */

/** Events kept (the oldest are dropped). */
export const DEBUG_RING_SIZE = 2000;

export interface RailTraceDebugEvent {
  /** ms since the page loaded. */
  t: number;
  kind: string;
  [k: string]: unknown;
}

let level = 0;
const ring: RailTraceDebugEvent[] = [];
let head = 0;
let total = 0;

const now = () => (typeof performance !== 'undefined' ? Math.round(performance.now()) : Date.now());

/** 0 = off, 1 = on, 2 = verbose (stations too). */
export function railTraceDebugLevel(): number {
  return level;
}

/** Read the switch (URL `?railTraceDebug=`, else localStorage `railTraceDebug`). Called at each trace start. */
export function refreshRailTraceDebug(): number {
  let raw: string | null = null;
  try {
    if (typeof location !== 'undefined') raw = new URLSearchParams(location.search).get('railTraceDebug');
  } catch {
    /* no URL */
  }
  if (raw == null) {
    try {
      raw = typeof localStorage !== 'undefined' ? localStorage.getItem('railTraceDebug') : null;
    } catch {
      /* storage blocked */
    }
  }
  const n = raw == null ? 0 : Number(raw);
  level = Number.isFinite(n) && n > 0 ? Math.min(2, Math.floor(n)) : raw === 'true' ? 1 : 0;
  return level;
}

/** Set the level directly (tests, or `__railTraceDebug.enable(2)` in the console). */
export function setRailTraceDebugLevel(n: number) {
  level = Math.max(0, Math.min(2, Math.floor(n)));
}

/** Record one event when the log is on at `minLevel` or above. */
export function railTraceDebug(kind: string, data: Record<string, unknown> = {}, minLevel = 1) {
  if (level < minLevel) return;
  const ev: RailTraceDebugEvent = { t: now(), kind, ...data };
  if (ring.length < DEBUG_RING_SIZE) ring.push(ev);
  else ring[head] = ev;
  head = (head + 1) % DEBUG_RING_SIZE;
  total++;
  try {
    console.debug('[railTrace]', kind, data);
  } catch {
    /* no console */
  }
}

/** The kept events, oldest first. */
export function railTraceDebugEvents(): RailTraceDebugEvent[] {
  return ring.length < DEBUG_RING_SIZE ? ring.slice() : [...ring.slice(head), ...ring.slice(0, head)];
}

/** How deep `errorInfo` follows an error's `cause` chain. */
const MAX_CAUSE_DEPTH = 5;

/**
 * An Error as plain JSON (message, name, stack, and its cause's). The cause
 * chain is followed at most `MAX_CAUSE_DEPTH` deep and stops at a cause seen
 * before (an error can be its own cause, or two can point at each other).
 */
export function errorInfo(e: unknown): Record<string, unknown> {
  return errorInfoAt(e, 0, new Set());
}

function errorInfoAt(e: unknown, depth: number, seen: Set<unknown>): Record<string, unknown> {
  if (e instanceof Error) {
    seen.add(e);
    const cause = e.cause;
    let causeInfo: Record<string, unknown> | undefined;
    if (cause !== undefined) {
      if (seen.has(cause)) causeInfo = { message: '[circular cause]' };
      else if (depth >= MAX_CAUSE_DEPTH) causeInfo = { message: '[cause chain truncated]' };
      else causeInfo = errorInfoAt(cause, depth + 1, seen);
    }
    return {
      name: e.name,
      message: e.message,
      stack: e.stack?.split('\n').slice(0, 12).join('\n'),
      ...(causeInfo ? { cause: causeInfo } : {}),
    };
  }
  return { message: String(e) };
}

/** The log as a JSON string (what the user sends). */
export function dumpRailTraceDebug(): string {
  return JSON.stringify(
    {
      version: 1,
      level,
      at: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      url: typeof location !== 'undefined' ? location.pathname : undefined,
      dropped: Math.max(0, total - ring.length),
      events: railTraceDebugEvents(),
    },
    (_k, v) => (typeof v === 'number' && !Number.isInteger(v) ? Math.round(v * 1e4) / 1e4 : v),
  );
}

export function clearRailTraceDebug() {
  ring.length = 0;
  head = 0;
  total = 0;
}

export interface RailTraceDebugApi {
  dump(): string;
  copy(): Promise<string>;
  clear(): void;
  enable(level?: number): void;
  events(): RailTraceDebugEvent[];
}

export const railTraceDebugApi: RailTraceDebugApi = {
  dump: dumpRailTraceDebug,
  copy: async () => {
    const s = dumpRailTraceDebug();
    try {
      await navigator.clipboard.writeText(s);
      return `copied ${s.length} characters`;
    } catch {
      // Clipboard refused (no focus / permission): the console prints it to copy by hand.
      console.log(s);
      return 'clipboard refused: the log is printed above';
    }
  },
  clear: clearRailTraceDebug,
  enable: (n = 1) => {
    setRailTraceDebugLevel(n);
    try {
      localStorage.setItem('railTraceDebug', String(n));
    } catch {
      /* storage blocked: on for this page only */
    }
  },
  events: railTraceDebugEvents,
};

if (typeof window !== 'undefined') {
  (window as unknown as { __railTraceDebug?: RailTraceDebugApi }).__railTraceDebug = railTraceDebugApi;
  refreshRailTraceDebug();
}
